import { useContext } from "react";
import { useSearchParams } from "react-router-dom";
import PrivateLayout from "@/components/layout/auth/PrivateLayout";
import { AuthContext } from "@/lib/auth/models/auth.model";
import { useI18n } from "@/hooks/useI18n";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { TabsHeader } from "@/components/ui/tabs-header";
import { Tabs, ProfileTabsContent } from "@/components/ui/tabs";
import { useSidebar } from "@/contexts/SidebarContext";
import { cn } from "@/components/utils/utils";
import {
  Activity,
  Bell,
  CloudCog,
  Loader2,
  RefreshCw,
  Rocket,
  XCircle,
} from "lucide-react";
import { useAdminStats } from "@/lib/admin/hooks/useEdgeFunctionStats";
import ReleasesTab from "@/lib/admin/ReleasesTab";
import EmailTemplatesTab from "@/lib/admin/email-templates/EmailTemplatesTab";
import MailProcessorAlert from "@/lib/admin/components/MailProcessorAlert";
import EdgeFunctionTable from "@/lib/admin/components/EdgeFunctionTable";
import QuotaCard from "@/lib/admin/components/QuotaCard";
import DatabaseCard from "@/lib/admin/components/DatabaseCard";
import DatabaseSizeSection from "@/lib/admin/components/DatabaseSizeSection";

// ─── Page ─────────────────────────────────────────────────────────────────────

/**
 * Tutte le sezioni admin vivono qui, come tab della stessa pagina. `notifications`
 * governa l'intera tabella `email_templates`: email e notifica in-app sono due
 * canali dello STESSO record, quindi un tab solo (prima erano due, `emails` e
 * `notifications`, che mostravano campi diversi della stessa riga).
 */
const ADMIN_TABS = ["releases", "system", "notifications"] as const;
type AdminTab = (typeof ADMIN_TABS)[number];

/** Vecchio tab email: i link salvati continuano ad aprire la tabella unificata. */
const LEGACY_TAB_ALIASES: Record<string, AdminTab> = { emails: "notifications" };

const AdminDashboard = () => {
  const authContext = useContext(AuthContext);
  if (!authContext) throw new Error("AdminDashboard must be used inside an AuthProvider");

  const { t } = useI18n();
  const { data, loading, error, load } = useAdminStats({ days: 7 });
  const { isSidebarCollapsed } = useSidebar();
  const [searchParams, setSearchParams] = useSearchParams();

  const tabParam = searchParams.get("tab");
  const normalizedTab = tabParam ? (LEGACY_TAB_ALIASES[tabParam] ?? tabParam) : null;
  const activeTab = ADMIN_TABS.includes(normalizedTab as AdminTab)
    ? (normalizedTab as AdminTab)
    : "releases";
  const handleTabChange = (value: string) => setSearchParams({ tab: value });

  const tabs = [
    { value: "releases", label: t("adminDashboard.tabs.releases"), icon: Rocket },
    { value: "system", label: t("adminDashboard.tabs.system"), icon: Activity },
    { value: "notifications", label: t("emailTemplates.tabs.notifications"), icon: Bell },
  ];

  const functions = data?.edge_functions.functions ?? [];
  const periodDays = data?.edge_functions.period_days ?? 7;

  /**
   * Il tab dei template contiene una tabella che scrolla al proprio interno e
   * deve arrivare al fondo dello schermo: solo per lui la catena
   * layout → tab → contenuto passa a flex full-height. Releases e Sistema restano
   * a flusso normale, altrimenti perderebbero lo scroll di pagina.
   */
  const isTemplatesTab = activeTab === "notifications";

  return (
    <PrivateLayout
      pageTitle={t("adminDashboard.pageTitle")}
      pageDescription={t("adminDashboard.pageDescription")}
      showBackButton={false}
      contentClassName={isTemplatesTab ? "h-full flex flex-col min-h-0 py-2 md:py-4" : undefined}
    >
      <div className={cn(isTemplatesTab ? "flex flex-1 flex-col min-h-0 gap-6" : "space-y-6")}>
        <TabsHeader
          activeTab={activeTab}
          onTabChange={handleTabChange}
          tabs={tabs}
          sidebarCollapsed={isSidebarCollapsed}
        />

        <Tabs
          value={activeTab}
          onValueChange={handleTabChange}
          className={cn("w-full", isTemplatesTab && "flex flex-1 flex-col min-h-0")}
        >
          <ProfileTabsContent value="releases">
            <ReleasesTab />
          </ProfileTabsContent>

          <ProfileTabsContent value="notifications" className="flex-1 min-h-0">
            <EmailTemplatesTab />
          </ProfileTabsContent>

          <ProfileTabsContent value="system">
            <div className="space-y-6">

              {/*
                Caricamento manuale: `admin-stats` interroga l'API di gestione
                Supabase, quindi parte solo su click come la sezione peso DB.
              */}
              <div className="flex justify-end">
                <button
                  onClick={() => load()}
                  disabled={loading}
                  className="inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium text-gray-600 border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-50 transition-colors"
                >
                  {loading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4" />
                  )}
                  {data
                    ? t("adminDashboard.stats.reloadButton")
                    : t("adminDashboard.stats.loadButton")}
                </button>
              </div>

              {/* Stato vuoto — prima del click */}
              {!loading && !error && !data && (
                <Card>
                  <CardContent>
                    <p className="text-sm text-gray-500 py-8 text-center">
                      {t("adminDashboard.stats.emptyState")}
                    </p>
                  </CardContent>
                </Card>
              )}

              {/* Mail-processor alert — shown only when error rate > 50% */}
              {!loading && !error && functions.length > 0 && (
                <MailProcessorAlert functions={functions} />
              )}

              {/* Loading state */}
              {loading && (
                <Card>
                  <CardContent>
                    <div className="flex items-center gap-2 py-10 text-gray-500 justify-center">
                      <Loader2 className="h-5 w-5 animate-spin" />
                      <span className="text-sm">{t("adminDashboard.loading")}</span>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Error state */}
              {!loading && error && (
                <Card>
                  <CardContent>
                    <div className="flex items-center gap-2 py-10 text-red-500 justify-center">
                      <XCircle className="h-5 w-5" />
                      <span className="text-sm">{error}</span>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Data loaded */}
              {!loading && !error && data && (
                <>
                  {/* Card 1 — Edge Functions */}
                  <Card>
                    <CardHeader className="flex flex-row items-center gap-3 pb-3">
                      <div className="flex items-center justify-center h-9 w-9 rounded-lg bg-blue-50 text-blue-600 flex-shrink-0">
                        <CloudCog className="h-5 w-5" />
                      </div>
                      <CardTitle className="text-base font-semibold">
                        {t("adminDashboard.edgeFunctions.title")}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {functions.length === 0 ? (
                        <p className="text-sm text-gray-500 py-6 text-center">
                          {t("adminDashboard.edgeFunctions.noData")}
                        </p>
                      ) : (
                        <EdgeFunctionTable functions={functions} periodDays={periodDays} />
                      )}
                    </CardContent>
                  </Card>

                  {/* Card 2 — Quota Piano */}
                  <QuotaCard usage={data.usage} />

                  {/* Card 3 — Database */}
                  <DatabaseCard db={data.db} />
                </>
              )}

              {/*
                Card 4 — Peso database e storage.
                Fuori dal blocco `data` perché ha un ciclo di vita proprio: le RPC
                sui cataloghi Postgres partono solo su click, non con la pagina.
              */}
              <DatabaseSizeSection />

            </div>
          </ProfileTabsContent>
        </Tabs>
      </div>
    </PrivateLayout>
  );
};

export default AdminDashboard;
